import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

import { FormikProps, useFormik } from 'formik'

import toastService from '@/components/toast/toast-service'
import { useGetBranchesData } from '@/hooks/queries/use-get-branch-data'
import {
  useDownloadDevolucoesReport,
  useGetDevolucoesList
} from '@/hooks/queries/use-get-devolucoes'
import { useGetUserData } from '@/hooks/queries/use-get-user-data'
import { IDadosBancarios } from '@/interfaces/beneficiarios'
import { Branch } from '@/interfaces/branches'
import {
  IDevolucao,
  IDevolucoesResultado,
  ILogsDevolucoes
} from '@/interfaces/devolucoes'
import { getDadosBancariosBeneficiario } from '@/services/beneficiarios'
import { downloadComprovante, getLogsDevolucoes } from '@/services/devolucoes'
import { createContext } from '@/utils/create-context'
import { useGetFeatureFlagData } from '@/hooks/queries/use-get-feature-flag-data'

interface IDevolucoesContextProps {
  branches: Branch[]
  dadosBancarios: IDadosBancarios | undefined
  devolucaoSelecionada: IDevolucao | undefined
  devolucoesList: IDevolucoesResultado | undefined
  downloadURL: { url: string } | undefined
  filterValues: IFormProps,
  formik: FormikProps<IFormProps>
  getFileFromDevolucoes: () => void
  handleAbrirHistorico: (devolucaoEmTratativa: IDevolucao) => void
  handleAbrirTratativas: (devolucaoEmTratativa: IDevolucao) => void
  handleConfirmarDevolucoesLista: () => void
  handleConfirmarPedidosModalOpen: () => void
  handleDownloadComprovante: ({
    params
  }: {
    params: {
      idItemVenda: string
      idUnidade: string
    }
  }) => Promise<void>
  handleMotivoDevolucaoModalOpen: () => void
  handleSelectedBranch: (selectedOption: any[]) => void
  isActionsOpen: boolean
  isConfirmarPedidosModalOpen: boolean
  isDetalhesImportacaoModalOpen: boolean
  isFeatureFlagEnabled: boolean
  isFetchingDevolucoes: boolean
  isHistoricoDevolucaoModalOpen: boolean
  isLoadingDevolucoes: boolean
  isMotivoDevolucaoModalOpen: boolean
  isResultadoModalOpen: boolean
  isTratativasOpen: boolean
  logsDevolucao: ILogsDevolucoes[]
  pageSize: number
  search: string
  setDevolucaoSelecionada: Dispatch<SetStateAction<IDevolucao | undefined>>
  setDevolucoesItemId: Dispatch<SetStateAction<string>>
  setIsActionsOpen: Dispatch<SetStateAction<boolean>>
  setIsConfirmarPedidosModalOpen: Dispatch<SetStateAction<boolean>>
  setIsDetalhesImportacaoModalOpen: Dispatch<SetStateAction<boolean>>
  setIsHistoricoDevolucaoModalOpen: Dispatch<SetStateAction<boolean>>
  setIsMotivoDevolucaoModalOpen: Dispatch<SetStateAction<boolean>>
  setIsResultadoModalOpen: Dispatch<SetStateAction<boolean>>
  setIsTratativasOpen: Dispatch<SetStateAction<boolean>>
  setPage: Dispatch<SetStateAction<number>>
  setPageSize: Dispatch<SetStateAction<number>>
  setSearch: Dispatch<SetStateAction<string>>
  totalItens: number
  totalPages: number
  userData: any
}

interface IFormProps {
  dataInicio: string
  dataFim: string
  situacao: string
  tipoDevolucao: string
  numeroPedido: string
  unidades: {
    value: string
    label: string
  }[]
  cpf: string
  idsUnidades?: string[]
}

const [useDevolucoesContext, PedidosProvider] =
  createContext<IDevolucoesContextProps>()

const DevolucoesContext = ({ children }: React.PropsWithChildren<{}>) => {
  const { data: userData } = useGetUserData()
  const { data: branches } = useGetBranchesData()
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSize] = useState<number>(10)
  const [devolucoesItemId, setDevolucoesItemId] = useState<string>('')
  const [devolucaoSelecionada, setDevolucaoSelecionada] = useState<IDevolucao>()
  const [isTratativasOpen, setIsTratativasOpen] = useState(false)
  const [dadosBancarios, setDadosBancarios] = useState<IDadosBancarios>()
  const [logsDevolucao, setLogsDevolucao] = useState<ILogsDevolucoes[]>()
  const [isActionsOpen, setIsActionsOpen] = useState(false)
  const [isMotivoDevolucaoModalOpen, setIsMotivoDevolucaoModalOpen] =
    useState<boolean>(false)
  const [isHistoricoDevolucaoModalOpen, setIsHistoricoDevolucaoModalOpen] =
    useState<boolean>(false)
  const [isConfirmarPedidosModalOpen, setIsConfirmarPedidosModalOpen] =
    useState<boolean>(false)
  const [isResultadoModalOpen, setIsResultadoModalOpen] =
    useState<boolean>(false)
  const [isDetalhesImportacaoModalOpen, setIsDetalhesImportacaoModalOpen] =
    useState<boolean>(false)
  const [filterValues, setFilterValues] = useState<IFormProps>({} as IFormProps)
  const [isFeatureFlagEnabled, setIsFeatureFlagEnabled] = useState<boolean>(false)

  const { data: featureFlagData } = useGetFeatureFlagData({
    employerId: userData?.authenticatedAccountId,
    featureFlag: 'devolucao-em-lote'
  });

  useEffect(() => {
    setIsFeatureFlagEnabled(featureFlagData?.sucesso)
  }, [featureFlagData]);

  useEffect(() => {
    page > 1 && setPage(1)
  }, [pageSize])

  const defaultBranchId =
    branches?.filter(
      item => item.branchId === userData?.authenticatedBranchId
    )[0]?.branchId || ''

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      dataInicio: new Date(new Date().setMonth(new Date().getMonth() - 1))
        .toISOString()
        .split('T')[0],
      dataFim: new Date().toISOString().split('T')[0],
      unidades:
        branches
          ?.filter(item => item.branchId === userData?.authenticatedBranchId)
          .map(item => ({
            value: item.branchId,
            label: `${item.name}-${item.cnpj}`
          })) || ([] as any),
      idsUnidades: [defaultBranchId]
    } as IFormProps,
    onSubmit: values => {
      setFilterValues({
        idsUnidades: values.unidades?.some(unit => unit.value === 'todos')
          ? branches?.map(branch => branch.branchId)
          : values.unidades?.map(item => item.value) || [],
        cpf: values.cpf,
        numeroPedido: values.numeroPedido,
        tipoDevolucao: values.tipoDevolucao,
        situacao: values.situacao,
        dataInicio: values.dataInicio,
        dataFim: values.dataFim,
        unidades: values.unidades
      })
    }
  })

  const [search, setSearch] = useState('')
  const inicio = (page - 1) * pageSize
  const {
    data: devolucoesList,
    isLoading: isLoadingDevolucoes,
    isFetching: isFetchingDevolucoes
  } = useGetDevolucoesList({
    params: {
      inicio: inicio,
      comprimento: pageSize,
      procurar: search,
      campo: 'Id',
      direcao: 'desc',
      idEmpregador: userData?.authenticatedAccountId ?? '',
      idsUnidades: formik.values.idsUnidades,
      ...filterValues,
      dataInicio: formik.values.dataInicio + 'T00:00:00',
      dataFim: formik.values.dataFim + 'T23:59:59'
    }
  })
  const totalPages = Math.ceil(
    (devolucoesList?.conteudo?.quantidadeDeItems ?? 0) / pageSize
  )
  const totalItens = devolucoesList?.conteudo?.resultados?.length ?? 0

  const handleSelectedBranch = (selectedOption: any) => {
    if (selectedOption?.some((option: any) => option.value === 'todos')) {
      const allBranchIds = branches?.map((branch: any) => ({
        value: branch.branchId,
        label: `${branch.name}-${branch.cnpj}`
      }))

      formik.setFieldValue('unidades', allBranchIds)

      formik.setFieldValue('unidades', [{ value: 'todos', label: 'Todas' }])
    } else {
      formik.setFieldValue('unidades', selectedOption)
    }
  }

  const { data: downloadURL, refetch: getFileFromDevolucoes } =
    useDownloadDevolucoesReport({ id: devolucoesItemId })

  const handleConfirmarPedidosModalOpen = () => {
    setIsConfirmarPedidosModalOpen(true)
  }

  const handleMotivoDevolucaoModalOpen = () => {
    setIsMotivoDevolucaoModalOpen(true)
  }

  const handleConfirmarDevolucoesLista = () => {
    toastService.success('Devoluções confirmadas com sucesso')
    setIsResultadoModalOpen(false)
  }

  const handleAbrirHistorico = (devolucaoEmTratativa: IDevolucao) => {
    setLogsDevolucao([])
    setIsHistoricoDevolucaoModalOpen(true)
    setDevolucaoSelecionada(devolucaoEmTratativa)
    getLogsDevolucoes({
      params: {
        inicio: 0,
        comprimento: 1000,
        campo: 'CriadoEm',
        direcao: 'desc',
        idGeralDevolucao: devolucaoEmTratativa?.idGeralDevolucao ?? ''
      }
    })
      .then(response => {
        setLogsDevolucao(response?.conteudo?.resultados)
      })
      .catch(error => {
        toastService.error(error)
      })
  }

  const handleAbrirTratativas = (devolucaoEmTratativa: IDevolucao) => {
    setIsTratativasOpen(true)
    setDevolucaoSelecionada(devolucaoEmTratativa)
    setDadosBancarios({
      idItemVenda: '',
      idPedidoVenda: '',
      idVenda: '',
      nome: '',
      cpf: '',
      numeroDoBanco: '',
      nomeDoBanco: '',
      tipoDeContaBancaria: '',
      agenciaBancaria: '',
      digitoDaAgenciaBancaria: '',
      contaBancaria: '',
      digitoDaContaBancaria: '',
      chavePix: '',
      tipoChavePix: ''
    })

    getDadosBancariosBeneficiario({
      params: {
        idPedido: devolucaoEmTratativa?.idPedido ?? '',
        idVenda: devolucaoEmTratativa?.idVenda ?? '',
        idItemVenda:
          devolucaoEmTratativa?.id ?? ''
      }
    })
      .then(response => {
        setDadosBancarios(response?.conteudo)
      })
      .catch(error => {
        toastService.error(error)
      })
  }

  const handleDownloadComprovante = async ({
    params
  }: {
    params: {
      idItemVenda: string
      idUnidade: string
    }
  }) => {
    downloadComprovante({
      params: {
        ...params,
        idUsuarioSolicitante: userData.userId ?? '',
        idEmpregador: userData?.authenticatedAccountId ?? ''
      }
    })
      .then(() => {
        toastService.success(
          'Download solicitado! O comprovante estará disponível em relatórios.'
        )
      })
      .catch(() => {
        toastService.error('Erro ao solicitar download do comprovante')
      })
  }

  const contextValue = useMemo(() => ({
    branches,
    dadosBancarios,
    devolucaoSelecionada,
    devolucoesList,
    downloadURL,
    filterValues,
    formik,
    getFileFromDevolucoes,
    handleAbrirHistorico,
    handleAbrirTratativas,
    handleConfirmarDevolucoesLista,
    handleConfirmarPedidosModalOpen,
    handleDownloadComprovante,
    handleMotivoDevolucaoModalOpen,
    handleSelectedBranch,
    isActionsOpen,
    isConfirmarPedidosModalOpen,
    isDetalhesImportacaoModalOpen,
    isFeatureFlagEnabled,
    isFetchingDevolucoes,
    isHistoricoDevolucaoModalOpen,
    isLoadingDevolucoes,
    isMotivoDevolucaoModalOpen,
    isResultadoModalOpen,
    isTratativasOpen,
    logsDevolucao,
    pageSize,
    search,
    setDevolucaoSelecionada,
    setDevolucoesItemId,
    setIsActionsOpen,
    setIsConfirmarPedidosModalOpen,
    setIsDetalhesImportacaoModalOpen,
    setIsHistoricoDevolucaoModalOpen,
    setIsMotivoDevolucaoModalOpen,
    setIsResultadoModalOpen,
    setIsTratativasOpen,
    setPage,
    setPageSize,
    setSearch,
    totalItens,
    totalPages,
    userData,
  }), [
    branches,
    dadosBancarios,
    devolucaoSelecionada,
    devolucoesList,
    downloadURL,
    filterValues,
    formik,
    getFileFromDevolucoes,
    handleAbrirHistorico,
    handleAbrirTratativas,
    handleConfirmarDevolucoesLista,
    handleConfirmarPedidosModalOpen,
    handleDownloadComprovante,
    handleMotivoDevolucaoModalOpen,
    handleSelectedBranch,
    isActionsOpen,
    isConfirmarPedidosModalOpen,
    isDetalhesImportacaoModalOpen,
    isFeatureFlagEnabled,
    isFetchingDevolucoes,
    isHistoricoDevolucaoModalOpen,
    isLoadingDevolucoes,
    isMotivoDevolucaoModalOpen,
    isResultadoModalOpen,
    isTratativasOpen,
    logsDevolucao,
    pageSize,
    search,
    setDevolucaoSelecionada,
    setDevolucoesItemId,
    setIsActionsOpen,
    setIsConfirmarPedidosModalOpen,
    setIsDetalhesImportacaoModalOpen,
    setIsHistoricoDevolucaoModalOpen,
    setIsMotivoDevolucaoModalOpen,
    setIsResultadoModalOpen,
    setIsTratativasOpen,
    setPage,
    setPageSize,
    setSearch,
    totalItens,
    totalPages,
    userData,
  ])

  return (
    <PedidosProvider value={contextValue}>
      {children}
    </PedidosProvider>
  )
}

export { DevolucoesContext, useDevolucoesContext }
